import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Search, ChevronDown, ChevronRight } from "lucide-react";
import AddVariableIcon from "../../icons/svg/add_variable.svg?react";

// ─── Types ─────────────────────────────────────────────────────────────────────

interface VariableItem {
  label: string;
  key: string;
}

interface VariableGroup {
  id: string;
  label: string;
  items: VariableItem[];
}

// ─── Build variable groups ──────────────────────────────────────────────────────
// Matches the structure from Editor.tsx > suggestSmartValues:
//   id: `{{${item.get("value")}}}`, value: item.get("value"), type: "smartField"
// The hardcoded contact fields mirror the Quill mention popup values.

function buildGroups(attributes: any[]): VariableGroup[] {
  const contactGroup: VariableGroup = {
    id: "contact",
    label: "Variables from Contact fields",
    items: [
      { label: "Given Name", key: "{{contact.first_name}}" },
      { label: "Family Name", key: "{{contact.last_name}}" },
      { label: "Email", key: "{{contact.email}}" },
      { label: "Position/Title", key: "{{contact.title}}" },
      { label: "Phone", key: "{{contact.phone}}" },
      { label: "Address", key: "{{contact.address}}" },
    ],
  };

  const customItems: VariableItem[] = [];
  if (attributes && attributes.length > 0) {
    attributes.forEach((attr: any) => {
      const value = typeof attr === "object" ? attr.value || attr.id || attr.fieldId : attr;
      const label = typeof attr === "object" ? attr.label || attr.name || value : value;
      if (value) {
        customItems.push({ label, key: `{{${value}}}` });
      }
    });
  }

  const groups: VariableGroup[] = [contactGroup];
  if (customItems.length > 0) {
    groups.push({ id: "custom", label: "Variables from Custom columns", items: customItems });
  }
  return groups;
}

// ─── Insert into Quill ──────────────────────────────────────────────────────────

function insertVariableIntoQuill(quill: any, key: string) {
  if (!quill) return;
  const range = quill.getSelection(true);
  const index = range ? range.index : quill.getLength();
  if (range && range.length > 0) quill.deleteText(range.index, range.length);
  quill.insertText(index, key, { color: "#16a34a" });
  quill.setSelection(index + key.length, 0);
  quill.focus();
}

// ─── Component ─────────────────────────────────────────────────────────────────

interface Props {
  quillRef: React.MutableRefObject<any>;
  openRef?: React.MutableRefObject<(() => void) | null>;
}

const EmailVariablePicker = ({ quillRef, openRef }: Props) => {
  const { t } = useTranslation();
  const attributes = useSelector((state: any) => state.template.get("attributes"));
  const attrJS = attributes ? attributes.toJS() : [];
  const groups = buildGroups(attrJS);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Start empty — synced via effect so new groups (e.g. "custom" that loads later) auto-expand
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Whenever groups change (e.g. attributes arrive from Redux), ensure all groups are expanded by default
  useEffect(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      groups.forEach((g) => {
        if (!(g.id in next)) next[g.id] = true;
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups.map((g) => g.id).join(",")]);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Compute fixed position on open (escape modal overflow)
  const handleOpen = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen((v) => !v);
  };

  // Expose open function to parent via ref
  useEffect(() => {
    if (openRef) {
      openRef.current = () => {
        if (triggerRef.current) {
          const rect = triggerRef.current.getBoundingClientRect();
          setPos({ top: rect.bottom + 4, left: rect.left });
        }
        setOpen(true);
        setSearch("");
      };
    }
  });

  // Close on outside click
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // Auto-focus search
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 60);
  }, [open]);

  const handleSelect = (key: string) => {
    insertVariableIntoQuill(quillRef.current, key);
    setOpen(false);
    setSearch("");
  };

  const toggleGroup = (id: string) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  // Filter groups by search
  const filteredGroups = groups
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (item) =>
          !search ||
          item.label.toLowerCase().includes(search.toLowerCase()) ||
          item.key.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <span className="relative inline-flex items-center align-middle">
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        title={t("Insert Variable")}
        onClick={handleOpen}
        className={`inline-flex items-center justify-center w-[28px] h-[24px] rounded transition-colors focus:outline-none cursor-pointer select-none
          ${open ? "text-blue-600 bg-blue-50" : "text-gray-700 hover:text-blue-600"}`}
      >
        <AddVariableIcon className="w-[15px] h-[15px] shrink-0 mt-0.5" />
      </button>

      {/* Dropdown panel — fixed to escape modal overflow */}
      {open && (
        <div
          ref={panelRef}
          className="fixed z-[200] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
          style={{ top: pos.top, left: pos.left, width: 320 }}
        >
          {/* Search */}
          <div className="px-4 pt-4 pb-0">
            <div className="flex items-center gap-2.5 pb-3 border-b border-gray-100">
              <Search size={15} className="text-gray-300 shrink-0" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("Search variables")}
                className="flex-1 bg-transparent outline-none text-sm text-gray-700 placeholder-gray-300"
              />
            </div>
          </div>

          {/* Variable groups */}
          <div style={{ maxHeight: 380, overflowY: "auto", overflowX: "hidden" }}>
            {filteredGroups.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400">
                {t("No variables found.")}
              </div>
            ) : (
              filteredGroups.map((group, groupIdx) => (
                <div
                  key={group.id}
                  style={{ borderTop: groupIdx > 0 ? "1px solid #e5e7eb" : "none" }}
                >
                  {/* Group header */}
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      padding: "12px 16px 0px 16px",
                      marginTop: "16px",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {group.label}
                    </span>
                    <span
                      style={{
                        marginLeft: "auto",
                        paddingLeft: 6,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {expanded[group.id] ? (
                        <ChevronDown size={14} color="#9ca3af" />
                      ) : (
                        <ChevronRight size={14} color="#9ca3af" />
                      )}
                    </span>
                  </button>

                  {/* Variable rows */}
                  {expanded[group.id] && (
                    <div>
                      {group.items.map((item, idx) => (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => handleSelect(item.key)}
                          className={`w-full flex items-center justify-between border-none cursor-pointer text-left focus:outline-none transition-colors hover:bg-gray-50 group ${idx === 0 ? "bg-gray-50" : "bg-transparent"}`}
                          style={{ padding: "15px 16px" }}
                        >
                          <span className="text-[15px] font-normal text-gray-900 group-hover:text-blue-600 transition-colors min-w-0 truncate flex-1">
                            {item.label}
                          </span>
                          <span
                            className="text-[11px] font-mono text-gray-400 group-hover:text-green-600 transition-colors ml-3 flex-shrink-0"
                            style={{
                              maxWidth: 110,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={item.key}
                          >
                            {item.key}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </span>
  );
};

export default EmailVariablePicker;
